import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { blogAPI } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Search, BookOpen, Clock, ArrowLeft, Loader2, ChevronLeft, ChevronRight, User } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Skeleton } from '../components/ui/skeleton';
import usePageTitle from '../hooks/usePageTitle';

const CATEGORIES = [
  { value: 'all', label: 'All' },
  { value: 'success_story', label: 'Success Stories' },
  { value: 'advice', label: 'Career Advice' },
  { value: 'resources', label: 'Resources' },
  { value: 'news', label: 'News' },
];

const Blog = () => {
  usePageTitle('Blog');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [page, setPage] = useState(1);
  const LIMIT = 12;

  useEffect(() => { fetchPosts(); }, [category, page]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: (page - 1) * LIMIT };
      if (category !== 'all') params.category = category;
      const res = await blogAPI.list(params);
      setPosts(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPost = async (post) => {
    // Try to get the full post content
    try {
      const res = await blogAPI.get(post.post_id);
      setSelectedPost(res.data);
    } catch (e) {
      setSelectedPost(post); // fallback to what we have
    }
  };

  const readingTime = (content) => {
    if (!content) return '1 min';
    const words = content.split(/\s+/).length;
    return `${Math.max(1, Math.ceil(words / 200))} min read`;
  };

  const filteredPosts = search
    ? posts.filter(p => p.title?.toLowerCase().includes(search.toLowerCase()))
    : posts;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="font-heading font-extrabold text-3xl text-dark-slate">Blog & Resources</h1>
          <p className="text-slate-gray mt-1">Alumni success stories, career advice, and resources</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-gray" />
            <Input placeholder="Search articles..." value={search} onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-11 rounded-xl bg-white border-slate-200" />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {CATEGORIES.map(cat => (
              <button key={cat.value} onClick={() => { setCategory(cat.value); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  category === cat.value ? 'bg-dark-slate text-white shadow-md' : 'bg-white text-slate-gray border border-slate-200 hover:bg-slate-50'
                }`}>{cat.label}</button>
            ))}
          </div>
        </div>

        {/* Posts */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
                <Skeleton className="w-full h-40" />
                <div className="p-5 space-y-3">
                  <Skeleton className="h-4 w-20 rounded-full" />
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="bg-white rounded-3xl p-12 text-center border border-slate-100 shadow-ambient">
            <BookOpen className="w-12 h-12 text-slate-gray mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl text-dark-slate mb-2">No articles found</h3>
            <p className="text-slate-gray">Check back soon for new content!</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {filteredPosts.map((post, index) => (
                <div key={post.post_id}
                  className={`bg-white rounded-2xl border border-slate-100 shadow-ambient hover:shadow-hover transition-shadow overflow-hidden cursor-pointer ${
                    index === 0 ? 'md:col-span-2 md:row-span-1' : ''
                  }`}
                  onClick={() => handleSelectPost(post)}>
                  {post.featured_image ? (
                    <img src={post.featured_image} alt={post.title} className={`w-full object-cover ${index === 0 ? 'h-56' : 'h-40'}`} />
                  ) : (
                    <div className={`w-full bg-gradient-to-br from-mint-fresh to-emerald-200 flex items-center justify-center ${index === 0 ? 'h-56' : 'h-40'}`}>
                      <BookOpen className="w-10 h-10 text-dark-slate/20" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="secondary" className="bg-blue-50 text-blue-700 text-xs">{(post.category || 'Article').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                      <span className="text-xs text-slate-gray flex items-center"><Clock className="w-3 h-3 mr-1" />{readingTime(post.content)}</span>
                    </div>
                    <h3 className={`font-heading font-bold text-dark-slate mb-2 ${index === 0 ? 'text-xl' : 'text-lg'} line-clamp-2`}>{post.title}</h3>
                    {post.excerpt && <p className="text-slate-gray text-sm line-clamp-2 mb-3">{post.excerpt}</p>}
                    <div className="flex items-center justify-between text-xs text-slate-gray">
                      <span className="flex items-center"><User className="w-3 h-3 mr-1" />{post.author_name || 'GradLink'}</span>
                      {post.created_at && <span>{formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-center space-x-3">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-full">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm text-slate-gray font-medium">Page {page}</span>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={filteredPosts.length < LIMIT} className="rounded-full">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </>
        )}

        {/* Post Detail Modal */}
        <Dialog open={!!selectedPost} onOpenChange={() => setSelectedPost(null)}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[85vh] overflow-y-auto">
            {selectedPost && (
              <>
                {selectedPost.featured_image && (
                  <img src={selectedPost.featured_image} alt={selectedPost.title} className="w-full h-48 object-cover rounded-xl mb-4" />
                )}
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-dark-slate leading-tight">{selectedPost.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-3">
                  <div className="flex items-center space-x-3 text-sm text-slate-gray">
                    <Badge variant="secondary" className="bg-blue-50 text-blue-700">{(selectedPost.category || 'Article').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
                    <span className="flex items-center"><Clock className="w-3 h-3 mr-1" />{readingTime(selectedPost.content)}</span>
                    <span className="flex items-center"><User className="w-3 h-3 mr-1" />{selectedPost.author_name || 'GradLink'}</span>
                  </div>
                  <div className="prose prose-slate max-w-none">
                    <p className="text-slate-gray whitespace-pre-wrap leading-relaxed">{selectedPost.content || selectedPost.excerpt}</p>
                  </div>
                  {selectedPost.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-100">
                      {selectedPost.tags.map((tag, i) => <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>)}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Blog;
